import { Pays } from "../../../interfaces-base";
import { fetchValidateZipCode, fetchValidatePhoneNumber, fetchValidateNumTva } from "../../../service/address.api";
import { AddressType, BaseForm, CustomerType, FormAddressValidatorFunction, FormKeys, ProForm, FetchState, FetchInfos } from "../fa-interfaces";
import { countryHasRequiredNumTvaIntra, countryHasRequiredSiret, clearNumTva, cleanWhitespace } from "./form.util";

const timeouts: Partial<Record<FormKeys, { clearFn: () => void }|undefined>> = {
  code_postal: undefined,
  telephone_mobile: undefined,
  telephone_fixe: undefined,
  numero_tva: undefined,
};

function getFieldsValidator(customerType: CustomerType): Record<FormKeys, undefined | FormAddressValidatorFunction> {
  return {
    id: undefined,
    libelle: undefined,
    nom: requiredValidator,
    prenom: requiredValidator,
    ville: requiredValidator,
    adresse: requiredValidator,
    adresse2: undefined,
    code_postal: zipCodeValidator,
    telephone_fixe: requiredOnePhoneNumberValidator('fixe'),
    telephone_mobile: requiredOnePhoneNumberValidator('mobile'),
    telephone_code_pays: undefined,
    is_livraison: undefined,
    is_facturation: undefined,
    pays_id: undefined,
    societe: customerType === 'pro' ? requiredValidator : undefined,
    categorie_juridique: undefined,
    code_ape: undefined,
    is_favorite: undefined,
    siret: validateSiret,
    numero_tva: validateNumTVA,
  }
}

const requiredValidator: FormAddressValidatorFunction = async (value, addressType, customerType, country, state, fetchEl) => {
  let result = undefined;
  if (value === undefined || value === null || cleanWhitespace(value).length === 0) {
    result = 'SHIPPING_FORM_REQUIRED_FIELD_ERROR';
    fetchEl.state?.loading && fetchEl.updateState(false, fetchEl.state.needToFetch, fetchEl.state.savedResponse);
  }
  return result;
};

const requiredOnePhoneNumberValidator = (type: 'mobile' | 'fixe'): FormAddressValidatorFunction => async (value, addressType, customerType, country, state, fetchEl) => {
  const fetchKey = type === 'mobile' ? 'telephone_mobile' : 'telephone_fixe';
  let result = undefined;
  timeouts[fetchKey]?.clearFn();

  if (await requiredValidator(state.telephone_fixe, addressType, customerType, country, state, fetchEl) && await requiredValidator(state.telephone_mobile, addressType, customerType, country, state, fetchEl)) {
    result = 'SHIPPING_FORM_REQUIRED_PHONE_ERROR';
  } else if (await requiredValidator(value, addressType, customerType, country, state, fetchEl) === undefined && fetchEl.state?.needToFetch) {
    result = await createPromiseValidate(
      () => fetchValidatePhoneNumber(country.codePays, type, value ?? ''),
      fetchEl,
      fetchKey
    );
  } else if (fetchEl.state?.savedResponse) {
    result = fetchEl.state?.savedResponse?.message;
  }
  return result;
};

const validateNumTVA: FormAddressValidatorFunction = async (value, addressType, customerType, country, state, fetchEl) => {
  let result = undefined;
  const formatedValue = clearNumTva(value ?? '', country);
  const vatChecker = new VATCheckerService();
  const mustCheck = customerType === 'pro' && countryHasRequiredNumTvaIntra(country.idPays, addressType);

  timeouts['numero_tva']?.clearFn();
  if (mustCheck) {
    result = await requiredValidator(formatedValue, addressType, customerType, country, state, fetchEl);
  }
  if (result === undefined && formatedValue.length > 0 && !vatChecker.validateFormat(country.codePaysTva, formatedValue)) {
    result = 'SHIPPING_FORM_INVALID_FORMAT_FIELD_ERROR';
    if (fetchEl.state?.loading) {
      fetchEl.updateState(false, fetchEl.state.needToFetch, fetchEl.state.savedResponse);
    }
  } else if (result === undefined && formatedValue.length > 0 && fetchEl.state?.needToFetch) {
    fetchEl.updateState(true, fetchEl.state.needToFetch, fetchEl.state.savedResponse);
    result = await createPromiseValidate(
      () => fetchValidateNumTva(country.codePays, formatedValue, state.societe ?? '', state.nom ?? ''),
      fetchEl,
      'numero_tva'
    );
  } else if (fetchEl.state?.savedResponse) {
    result = fetchEl.state?.savedResponse?.message;
  }

  return result;
};

function isValidSiret(value: string): boolean {
  let valid = false;
  const siret = cleanWhitespace(value);

  // Cas particulier pour les Siret de la POSTE (Sauf le siège de la poste)
  if ('35600000000048' !== siret && /^356000000/.test(siret)) {
    const sum = siret
      .split('')
      .reduce((prev, current): number => {
        return prev + parseInt(current)
      }, 0);
    // L'addition des chifres du siret pour ce cas particulier doit être un multiple de 5
    valid = sum % 5 === 0;
  } else {
    const sum = siret
      .split('')
      .reduce((prev, current, index): string => {
        const casted = parseInt(current);
        return prev.concat((index % 2 === 0 ? casted * 2 : casted).toString());
      }, '') // Ce reduce permet de reformater le siret (chaque position pair est multiplié par 2, si impair on multiplie par 1)
      .split('')
      .reduce((prev, current): number => {
        return prev + parseInt(current)
      }, 0); // Ce reduce permet d'additionner chaque chiffre du Siret reformat

    // L'addition des chifres reformat du siret doit être un multiple de 10
    valid = sum % 10 === 0;
  }

  return valid;
}

const validateSiret: FormAddressValidatorFunction = async (value, addressType, customerType, country, state, fetchEl) => {
  let result = undefined;
  const regex = new RegExp(/\d{14}/);
  if (customerType === 'pro' && countryHasRequiredSiret(country, addressType, state.code_postal)) {
    result = await requiredValidator(value, addressType, customerType, country, state, fetchEl);
  }
  if (result === undefined && value !== undefined && value.length > 0 && !regex.test(cleanWhitespace(value))) {
    result = 'SHIPPING_FORM_INVALID_FORMAT_FIELD_ERROR';
  } else if (result === undefined && value !== undefined && value.length > 0 && !isValidSiret(value)) {
    result = 'SHIPPING_FORM_INVALID_FORMAT_SIRET_ERROR';
  }
  return result;
};

const zipCodeValidator: FormAddressValidatorFunction = async (value, addressType, customerType, country, state, fetchEl) => {
  let result = await requiredValidator(value, addressType, customerType, country, state, fetchEl);
  timeouts['code_postal']?.clearFn();
  if (result === undefined && fetchEl.state?.needToFetch) {
    result = await createPromiseValidate(
      () => fetchValidateZipCode(country.codePays, value ?? ''),
      fetchEl,
      'code_postal'
    );
  } else if (result === undefined && fetchEl.state?.savedResponse) {
    result = fetchEl.state.savedResponse?.message;
  }
  return result;
};

export async function getFieldError(field: FormKeys, value: string | undefined, addressType: AddressType, customerType: CustomerType, country: Pays, state: Partial<BaseForm & ProForm>, fetchEl: FetchState<FetchInfos>): Promise<string|undefined> {
  return getFieldsValidator(customerType)[field]?.(value, addressType, customerType, country, state, fetchEl);
}

export async function getFieldsError(
  addressType: AddressType, customerType: CustomerType, country: Pays, state: Partial<BaseForm & ProForm>,
  fetchStates: Partial<Record<FormKeys, FetchInfos | undefined>>, updateCallback: (key: FormKeys) => (loading: boolean, needToFetch: boolean, error: string | undefined) => void
): Promise<Partial<Record<FormKeys, string | undefined>>> {
  const result: Partial<Record<FormKeys, string | undefined>> = {};
  const keys = Object.keys(state) as FormKeys[];
  for (const key of keys) {
    result[key] = await getFieldError(
      key,
      state?.[key]?.toString() ?? undefined,
      addressType,
      customerType,
      country,
      state,
      { state: fetchStates?.[key], updateState: updateCallback(key) }
    );
  }
  return result;
}

async function createPromiseValidate(promiseValidate: () => Promise<{ message?: string }>, fetchEl: FetchState<FetchInfos>, timeoutKey: FormKeys): Promise<string | undefined> {
  fetchEl.updateState(true, fetchEl.state?.needToFetch ?? false, fetchEl.state?.savedResponse);
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      promiseValidate()
        .then(res => res)
        .catch(error => error)
        .then((data) => {
          fetchEl.updateState(false, false, data);
          resolve(data?.message);
        });
    }, 250);
    timeouts[timeoutKey] = { clearFn: () => {
      clearTimeout(timeout);
      resolve(undefined);
    }};
  });
}